import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader2, BarChart2, Star, ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Colour palette (level 1-4) ──────────────────────────────────────────────
const PALETTE = [
    { selBg: 'bg-red-50',   selBorder: 'border-red-400',   dot: 'bg-red-400',   label: 'text-red-700',   icon: '○', hover: 'hover:border-red-300'   },
    { selBg: 'bg-amber-50', selBorder: 'border-amber-400', dot: 'bg-amber-400', label: 'text-amber-700', icon: '◑', hover: 'hover:border-amber-300' },
    { selBg: 'bg-blue-50',  selBorder: 'border-blue-400',  dot: 'bg-blue-400',  label: 'text-blue-700',  icon: '◕', hover: 'hover:border-blue-300'  },
    { selBg: 'bg-green-50', selBorder: 'border-green-500', dot: 'bg-green-500', label: 'text-green-700', icon: '●', hover: 'hover:border-green-300' },
];

const RATING_TEXT   = ['text-red-500', 'text-amber-500', 'text-blue-500', 'text-green-600'];
const MATURITY_LABELS = { 1: 'Inceptive', 2: 'Transient', 3: 'Stable', 4: 'Dynamic' };

// ─── Deterministic question-type assignment (per sub-question id) ─────────────
const SUB_TYPES = ['mcq', 'rating', 'truefalse', 'mcq', 'mcq', 'rating', 'truefalse'];

function getSubType(questionId = '') {
    let h = 0;
    for (let i = 0; i < questionId.length; i++) h = (h * 31 + questionId.charCodeAt(i)) & 0xffffffff;
    return SUB_TYPES[Math.abs(h) % SUB_TYPES.length];
}

// ─── MCQ Sub-question ─────────────────────────────────────────────────────────
function MCQSub({ question, selected, onSelect }) {
    return (
        <div className="space-y-2">
            {question.options.map((opt) => {
                const p = PALETTE[opt.value - 1] || PALETTE[0];
                const isSelected = selected === opt.value;
                return (
                    <button
                        key={opt.value}
                        onClick={() => onSelect(opt.value)}
                        className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer group
                            ${isSelected ? `${p.selBg} ${p.selBorder} shadow-sm` : `bg-white border-gray-100 ${p.hover} hover:bg-gray-50/50`}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all
                                ${isSelected ? `${p.dot} border-transparent` : 'border-gray-300 group-hover:border-gray-400'}`}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <span className={`flex-1 text-sm font-medium leading-snug ${isSelected ? p.label : 'text-gray-700'}`}>
                                {opt.label}
                            </span>
                            {isSelected && <CheckCircle className={`w-4 h-4 flex-shrink-0 ${p.label}`} />}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

// ─── Rating track Sub-question ────────────────────────────────────────────────
function RatingSub({ question, selected, onSelect }) {
    const [hovered, setHovered] = useState(null);
    const active = hovered ?? selected;

    return (
        <div>
            {/* 4-step track */}
            <div className="relative flex items-end justify-between mb-4 px-2">
                <div className="absolute top-5 left-8 right-8 h-0.5 bg-gray-100" />
                <div
                    className="absolute top-5 left-8 h-0.5 bg-gradient-to-r from-[#D67C2C] to-[#e89d4f] transition-all duration-300"
                    style={{ width: active ? `${((active - 1) / 3) * 100}%` : '0%' }}
                />
                {question.options.map((opt) => {
                    const isActive   = active >= opt.value;
                    const isSelected = selected === opt.value;
                    return (
                        <button
                            key={opt.value}
                            onClick={() => onSelect(opt.value)}
                            onMouseEnter={() => setHovered(opt.value)}
                            onMouseLeave={() => setHovered(null)}
                            className="relative flex flex-col items-center gap-1.5 cursor-pointer z-10 max-w-[22%]"
                        >
                            <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-200
                                ${isActive ? 'bg-[#D67C2C] border-[#D67C2C] shadow-md shadow-[#D67C2C]/30' : 'bg-white border-gray-200 hover:border-[#D67C2C]/50'}
                                ${isSelected ? 'scale-110' : ''}`}>
                                <Star className={`w-4 h-4 transition-colors ${isActive ? 'text-white fill-white' : 'text-gray-300'}`} />
                            </div>
                            <span className={`text-xs font-semibold text-center leading-tight transition-colors line-clamp-2 ${isActive ? RATING_TEXT[opt.value - 1] : 'text-gray-400'}`}>
                                {opt.label}
                            </span>
                        </button>
                    );
                })}
            </div>
            {/* Active descriptor pill */}
            {active && (
                <div className={`mt-2 px-3 py-2 rounded-lg border text-xs leading-relaxed transition-all duration-300
                    ${PALETTE[active - 1].selBg} ${PALETTE[active - 1].selBorder} ${PALETTE[active - 1].label}`}>
                    {question.options.find(o => o.value === active)?.label}
                </div>
            )}
        </div>
    );
}

// ─── True/False (2×2 grid) Sub-question ──────────────────────────────────────
function TrueFalseSub({ question, selected, onSelect }) {
    return (
        <div className="grid grid-cols-2 gap-2.5">
            {question.options.map((opt) => {
                const p = PALETTE[opt.value - 1] || PALETTE[0];
                const isSelected = selected === opt.value;
                return (
                    <button
                        key={opt.value}
                        onClick={() => onSelect(opt.value)}
                        className={`p-3.5 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer
                            ${isSelected ? `${p.selBg} ${p.selBorder} shadow-sm` : `bg-white border-gray-100 ${p.hover}`}`}
                    >
                        <div className={`text-lg mb-1.5 font-bold transition-colors ${isSelected ? p.label : 'text-gray-300'}`}>
                            {p.icon}
                        </div>
                        <p className={`font-semibold text-xs leading-snug ${isSelected ? p.label : 'text-gray-700'}`}>
                            {opt.label}
                        </p>
                        {isSelected && (
                            <div className="mt-1.5 flex justify-center">
                                <CheckCircle className={`w-3.5 h-3.5 ${p.label}`} />
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

// ─── Gamified sub-question wrapper ────────────────────────────────────────────
function GamifiedSubQuestion({ question, questionNumber, selected, onSelect }) {
    const type = getSubType(question.question_id);

    const renderInput = () => {
        switch (type) {
            case 'rating':    return <RatingSub    question={question} selected={selected} onSelect={onSelect} />;
            case 'truefalse': return <TrueFalseSub question={question} selected={selected} onSelect={onSelect} />;
            case 'mcq':
            default:          return <MCQSub       question={question} selected={selected} onSelect={onSelect} />;
        }
    };

    return (
        <div className="p-5">
            <p className="text-sm font-medium text-gray-800 mb-4 leading-relaxed">
                <span className="text-[#D67C2C] font-bold mr-2">Q{questionNumber}.</span>
                {question.question_text}
            </p>
            {renderInput()}
        </div>
    );
}

// ─── Category label map ───────────────────────────────────────────────────────
const CATEGORY_ORDER = ['awareness', 'documentation', 'implementation', 'monitoring', 'capacity_building', 'data_use', 'improvement', 'stakeholder', 'outcomes', 'innovation'];

const CATEGORY_LABEL = {
    awareness:         'Awareness & Understanding',
    documentation:     'Documentation & Policies',
    implementation:    'Implementation',
    monitoring:        'Monitoring & Review',
    capacity_building: 'Capacity Building',
    data_use:          'Data Use & Analysis',
    improvement:       'Improvement Planning',
    stakeholder:       'Stakeholder Engagement',
    outcomes:          'Outcomes & Results',
    innovation:        'Innovation & Excellence',
    general:           'General',
};

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * DetailedQuestionPage
 *
 * Renders all sub-questions for one indicator with gamified MCQ / Rating / True-False styles.
 *
 * Props:
 *   indicator      - { indicator_id, statement }
 *   questions      - [{ question_id, question_text, options:[{value,label}], category }]
 *   savedResponses - {question_id: value} | [{question_id, selected_value}] | null
 *   onComplete     - (indicatorId, [{question_id, selected_value}], computedLevel) => void
 *   onProgress     - (indicatorId, [{question_id, selected_value}], nextQuestionIndex) => void
 *                    called after every answer that does NOT finish the indicator, so a
 *                    half-answered indicator survives closing the page
 *   onPositionChange - (indicatorId, questionIndex) => void, to remember where the user is
 *   initialIndex   - question to open on (resume); defaults to the first unanswered one
 *   isSaving       - bool
 *   justSaved      - bool
 */
const DetailedQuestionPage = ({ indicator, questions, savedResponses, onComplete, onProgress, onPositionChange, initialIndex, isSaving, justSaved }) => {
    const [answers, setAnswers] = useState(() => {
        if (!savedResponses) return {};
        if (Array.isArray(savedResponses)) {
            const m = {};
            savedResponses.forEach(r => { m[r.question_id] = r.selected_value; });
            return m;
        }
        return { ...savedResponses };
    });
    // Same ordering the pager uses below (by category), needed here so "first
    // unanswered" matches what the user sees.
    const [currentIndex, setCurrentIndex] = useState(() => {
        const byCat = questions.reduce((acc, q) => {
            const cat = q.category || 'general';
            (acc[cat] = acc[cat] || []).push(q);
            return acc;
        }, {});
        const cats = CATEGORY_ORDER.filter(c => byCat[c]).concat(Object.keys(byCat).filter(c => !CATEGORY_ORDER.includes(c)));
        const ordered = cats.flatMap(c => byCat[c] || []);
        if (Number.isInteger(initialIndex) && initialIndex >= 0 && initialIndex < ordered.length) return initialIndex;
        const saved = Array.isArray(savedResponses)
            ? Object.fromEntries(savedResponses.map(r => [r.question_id, r.selected_value]))
            : (savedResponses || {});
        const firstOpen = ordered.findIndex(q => saved[q.question_id] === undefined);
        return firstOpen === -1 ? 0 : firstOpen;
    });

    useEffect(() => { onPositionChange?.(indicator.indicator_id, currentIndex); }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const totalQs    = questions.length;
    const answeredQs = Object.keys(answers).filter(k => questions.some(q => q.question_id === k)).length;
    const allAnswered = answeredQs === totalQs && totalQs > 0;
    const progressPct = totalQs > 0 ? Math.round((answeredQs / totalQs) * 100) : 0;

    const computeLevel = (ans) => {
        const vals = Object.values(ans).filter(v => v >= 1 && v <= 4);
        if (!vals.length) return null;
        return Math.max(1, Math.min(4, Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)));
    };

    const previewLevel = computeLevel(answers);

    const handleSelect = (questionId, value) => {
        const newAnswers = { ...answers, [questionId]: value };
        setAnswers(newAnswers);
        // Count only answers that belong to current questions (guards against stale keys)
        const validCount = Object.keys(newAnswers).filter(k => questions.some(q => q.question_id === k)).length;
        if (validCount === totalQs && totalQs > 0) {
            const subResponses = questions.map(q => ({ question_id: q.question_id, selected_value: newAnswers[q.question_id] }));
            onComplete(indicator.indicator_id, subResponses, computeLevel(newAnswers));
        } else if (onProgress) {
            // Not finished yet: persist what's answered so far + where to resume.
            const partial = questions
                .filter(q => newAnswers[q.question_id] !== undefined)
                .map(q => ({ question_id: q.question_id, selected_value: newAnswers[q.question_id] }));
            onProgress(indicator.indicator_id, partial, Math.min(currentIndex + 1, totalQs - 1));
        }
        // Auto-advance to the next sub-question shortly after answering (unless it's the last one)
        if (currentIndex < totalQs - 1) {
            setTimeout(() => setCurrentIndex(i => Math.min(i + 1, totalQs - 1)), 350);
        }
    };

    // Group by category
    const grouped = questions.reduce((acc, q) => {
        const cat = q.category || 'general';
        (acc[cat] = acc[cat] || []).push(q);
        return acc;
    }, {});
    const categoryKeys = CATEGORY_ORDER.filter(c => grouped[c]).concat(Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c)));

    // Flatten the category grouping into one ordered sequence for one-at-a-time pagination
    const orderedQuestions = categoryKeys.flatMap(cat => grouped[cat] || []);
    const currentQuestion = orderedQuestions[currentIndex];
    const currentCategory = currentQuestion ? (currentQuestion.category || 'general') : null;

    const goPrev = () => setCurrentIndex(i => Math.max(0, i - 1));
    const goNext = () => setCurrentIndex(i => Math.min(orderedQuestions.length - 1, i + 1));

    return (
        <div className="space-y-5">
            {/* Header card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <span className="inline-block text-xs font-bold text-[#D67C2C] bg-orange-50 px-2.5 py-1 rounded-md mb-3">
                    {indicator.indicator_id}
                </span>
                <h2 className="text-base sm:text-lg font-semibold text-gray-800 leading-relaxed mb-5">
                    {indicator.statement}
                </h2>

                <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500">{answeredQs} of {totalQs} sub-questions answered</span>
                        <div className="flex items-center gap-2">
                            {isSaving  && <Loader2   className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                            {!isSaving && justSaved  && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                            {previewLevel && (
                                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border
                                    ${PALETTE[previewLevel - 1].selBg} ${PALETTE[previewLevel - 1].selBorder} ${PALETTE[previewLevel - 1].label}`}>
                                    Preview: Level {previewLevel} — {MATURITY_LABELS[previewLevel]}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full rounded-full bg-[#D67C2C] transition-all duration-500" style={{ width: `${progressPct}%` }} />
                    </div>
                    {allAnswered && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                            <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            All sub-questions answered. Click "Next Question" to continue.
                        </div>
                    )}
                </div>
            </div>

            {/* Single sub-question, paginated */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Dot navigator */}
                <div className="px-6 pt-5 pb-1 flex items-center justify-center gap-2 flex-wrap">
                    {orderedQuestions.map((q, idx) => {
                        const isAnswered = answers[q.question_id] !== undefined;
                        const isCurrent = idx === currentIndex;
                        return (
                            <button
                                key={q.question_id}
                                onClick={() => setCurrentIndex(idx)}
                                title={`Question ${idx + 1}`}
                                className={`w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition-all duration-150
                                    ${isCurrent ? 'ring-2 ring-[#D67C2C] ring-offset-1' : ''}
                                    ${isAnswered ? 'bg-[#D67C2C] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                                {idx + 1}
                            </button>
                        );
                    })}
                </div>

                {currentQuestion && (
                    <>
                        <div className="px-5 pt-3">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                                <BarChart2 className="w-3.5 h-3.5" />
                                {CATEGORY_LABEL[currentCategory] || (currentCategory || '').replace(/_/g, ' ')}
                            </span>
                        </div>
                        <GamifiedSubQuestion
                            question={currentQuestion}
                            questionNumber={currentIndex + 1}
                            selected={answers[currentQuestion.question_id]}
                            onSelect={(val) => handleSelect(currentQuestion.question_id, val)}
                        />
                    </>
                )}

                {/* Previous / Next sub-question controls */}
                <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-gray-100">
                    <button
                        onClick={goPrev}
                        disabled={currentIndex === 0}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <span className="text-xs text-gray-400">
                        Question {currentIndex + 1} of {orderedQuestions.length}
                    </span>
                    <button
                        onClick={goNext}
                        disabled={currentIndex >= orderedQuestions.length - 1}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Next <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DetailedQuestionPage;
